import { matchPath } from 'react-router-dom'

// Resolução pura da rota: sem cliente HTTP, para os testes carregarem.

/** O cliente (nível CNPJ) que a tela atual está mostrando. */
export interface InsightsScope {
  organizationId: string
  /**
   * O contrato da tela, quando o botão foi aberto de dentro de um contrato. O
   * painel abre no cliente com este contrato já escolhido; o seletor continua a
   * oferecer os outros contratos do cliente.
   */
  workspaceId?: string
  /**
   * O cliente é a própria organização logada: o usuário é o RH da empresa
   * cliente (buyer). A lista de contratos dele não sai de
   * `/v2/organizations/{própria organização}/workspaces`, que o backend recusa
   * (seller e buyer iguais, 400): sai dos fornecedores do menu, como na rota
   * `/insights`. Ver `listsViaSellers`.
   */
  isOwnOrganization?: boolean
}

/**
 * As telas de contratos do cliente.
 *
 * O Insights entra no nível CNPJ: o painel tem seletor de contrato e é dele que
 * os relatórios saem.
 */
const CLIENT_ROUTES = [
  '/v2/organizations/contracts/:sellerId/:buyerId',
  '/v2/organizations/:organizationId/workspaces',
]

/**
 * Qualquer tela dentro de um contrato (atividades, aprovação, exportação...).
 *
 * why: o usuário passa o dia dentro do contrato, e o botão só existia na lista
 * de contratos; quem queria o Ciclo de Movimentação tinha de sair do trabalho
 * para o encontrar (pedido do Ruben, 25/09). Dentro do contrato o painel abre
 * no cliente, com o contrato da tela pré-selecionado: a escolha de contrato,
 * que era o motivo de não haver botão aqui, continua no seletor.
 */
const CONTRACT_ROUTE = '/v2/workspaces/:workspaceId'

/** `/v2/workspaces/:buyerId/create` cria contrato: o id ali é do cliente. */
const CREATE_CONTRACT_ROUTE = '/v2/workspaces/:buyerId/create'

export type InsightsRoute =
  | { kind: 'cliente'; organizationId: string }
  | { kind: 'contrato'; workspaceId: string }
  | null

/** Que tipo de tela é esta para o Insights, sem ir ao servidor. */
export function resolveInsightsRoute(pathname: string): InsightsRoute {
  const client = matchPath<{ buyerId?: string; organizationId?: string }>(
    pathname,
    { path: CLIENT_ROUTES, exact: true }
  )
  if (client) {
    // A rota buyer/seller carrega os dois ids; quem tem dado no Snowflake é o
    // comprador.
    const organizationId = client.params.buyerId ?? client.params.organizationId
    return organizationId ? { kind: 'cliente', organizationId } : null
  }

  if (matchPath(pathname, { path: CREATE_CONTRACT_ROUTE, exact: true })) {
    return null
  }

  const contract = matchPath<{ workspaceId: string }>(pathname, {
    path: CONTRACT_ROUTE,
    exact: false,
  })
  return contract?.params.workspaceId
    ? { kind: 'contrato', workspaceId: contract.params.workspaceId }
    : null
}

/**
 * O escopo das telas de cliente, que a rota resolve sozinha. Dentro de um
 * contrato o cliente vem do servidor: ver `useInsightsScope`.
 *
 * `null` é o que esconde o botão flutuante.
 */
export function resolveInsightsScope(pathname: string): InsightsScope | null {
  const route = resolveInsightsRoute(pathname)
  return route?.kind === 'cliente' ? { organizationId: route.organizationId } : null
}

/**
 * O escopo do botão, dada a rota e o que se sabe do contrato e do usuário.
 *
 * Corretora (seller): na tela de contratos, o cliente é o da rota; dentro de um
 * contrato, é o comprador daquele contrato, que vem do servidor.
 *
 * RH da empresa cliente (buyer), por decisão do Ruben (30/09): o botão aparece
 * nas mesmas telas, e o cliente é sempre a própria organização logada. O id da
 * rota não serve: na tela de contratos do RH ele é o do fornecedor (o menu
 * "Contratos" leva a `/v2/organizations/{fornecedor}/workspaces`), e recortar
 * por ele esvaziava o painel. Dentro do contrato não é preciso esperar o
 * servidor: o backend só entrega ao RH contratos em que ele é o comprador.
 */
export function insightsScopeFor(
  route: InsightsRoute,
  usuario: { buyerId?: string; isSeller: boolean; ownOrganizationId?: string }
): InsightsScope | null {
  if (!route) return null

  if (!usuario.isSeller) {
    if (!usuario.ownOrganizationId) return null
    const own = { organizationId: usuario.ownOrganizationId, isOwnOrganization: true }
    return route.kind === 'contrato' ? { ...own, workspaceId: route.workspaceId } : own
  }

  if (route.kind === 'cliente') return { organizationId: route.organizationId }
  if (route.kind === 'contrato' && usuario.buyerId) {
    return { organizationId: usuario.buyerId, workspaceId: route.workspaceId }
  }
  return null
}
